import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Header, Filter } from './types';
import { useFilterBuilder } from './useFilterBuilder';
import FieldsDropdown from './FieldsDropdown';
import FiltersDropdown from './FiltersDropdown';
import SelectOptionsDropdown from './SelectOptionsDropdown';
import * as S from './FilterMenu.style';
import CurrencyInput from '../CurrencyInput';

interface FilterMenuProps {
  headers: Header[];
  onApplyFilter: (filter: Filter) => void;
  onClose: () => void;
  filters?: Filter[];
  anchorRef: React.MutableRefObject<HTMLDivElement | null>;
  align?: 'center' | 'start' | 'end';
}

type MenuPosition = {
  top: number;
  left: number;
  transform: string;
};

const VIEWPORT_PADDING = 12;
const MENU_ESTIMATED_WIDTH = 280;

const computeMenuPosition = (
  anchorRect: DOMRect,
  menuWidth: number,
  align: 'center' | 'start' | 'end',
): MenuPosition => {
  const top = anchorRect.bottom + 10;
  let left = anchorRect.left + anchorRect.width / 2;
  let transform = 'translateX(-50%)';

  if (align === 'end') {
    left = anchorRect.right;
    transform = 'translateX(-100%)';
    if (left - menuWidth < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING + menuWidth;
    }
  } else if (align === 'start') {
    left = anchorRect.left;
    transform = 'none';
    if (left + menuWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - VIEWPORT_PADDING - menuWidth;
    }
  } else {
    const halfWidth = menuWidth / 2;
    if (left - halfWidth < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING + halfWidth;
    } else if (left + halfWidth > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - VIEWPORT_PADDING - halfWidth;
    }
  }

  return { top, left, transform };
};

const FilterMenu: React.FC<FilterMenuProps> = ({
  headers,
  onApplyFilter,
  onClose,
  filters = [],
  anchorRef,
  align = 'center',
}) => {
  const {
    selectedField,
    selectedFilterType,
    filterValue,
    displayValue,
    setFilterValue,
    setDisplayValue,
    handleSelectField,
    handleSelectFilterType,
    handleApply: baseHandleApply
  } = useFilterBuilder({ onApplyFilter });
  const [intervalValue, setIntervalValue] = useState({ start: '', end: '' });
  const [priceInputVal, setPriceInputVal] = useState<string>('');
  const [priceUnitVal, setPriceUnitVal] = useState<string>('');

  useEffect(() => {
    setPriceInputVal('');
    setPriceUnitVal('');
  }, [selectedField]);

  const handleApply = () => {
    if (!selectedField || !selectedFilterType) return;
    if (selectedField.type === 'price') {
      if (!priceInputVal) return;
      const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(parseFloat(priceInputVal));

      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: priceUnitVal ? { price: priceInputVal, unit: priceUnitVal } : priceInputVal,
        displayValue: priceUnitVal ? `${formattedPrice}/${priceUnitVal}` : formattedPrice,
        fieldType: 'price',
      };
      onApplyFilter(newFilter);
      setPriceInputVal('');
      setPriceUnitVal('');
      return;
    }
    if (selectedField.type === 'date' && selectedFilterType === 'Intervalo') {
      if (!intervalValue.start || !intervalValue.end) return;
      const formatDateBR = (dateStr: string): string => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts;
          return `${day}/${month}/${year}`;
        }
        return dateStr;
      };
      const newFilter: Filter = {
        key: selectedField.key,
        label: selectedField.label,
        filterType: selectedFilterType,
        value: { start: intervalValue.start, end: intervalValue.end },
        displayValue: `${formatDateBR(intervalValue.start)} até ${formatDateBR(intervalValue.end)}`,
        fieldType: selectedField.type,
      };
      onApplyFilter(newFilter);
      setIntervalValue({ start: '', end: '' });
      return;
    }
    baseHandleApply();
  };

  const [isFieldsDropdownOpen, setFieldsDropdownOpen] = useState(false);
  const [isFiltersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const [isSelectOptionsDropdownOpen, setSelectOptionsDropdownOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(() => {
    const anchor = anchorRef.current;
    if (!anchor) return null;
    return computeMenuPosition(anchor.getBoundingClientRect(), MENU_ESTIMATED_WIDTH, align);
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;

    if (!anchor) {
      setMenuPosition(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? MENU_ESTIMATED_WIDTH;
    setMenuPosition(computeMenuPosition(rect, menuWidth, align));
  }, [anchorRef, align]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, isFieldsDropdownOpen, isFiltersDropdownOpen, isSelectOptionsDropdownOpen, selectedField, selectedFilterType]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isClickOnAnchor = anchorRef.current && anchorRef.current.contains(event.target as Node);
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && !isClickOnAnchor) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const onApplyAndClose = () => {
    handleApply();
    onClose();
  }

  const availableHeaders = headers.filter(
    h => !filters.some(f => f.key === h.key)
  );

  if (!menuPosition) {
    return null;
  }

  const menuContent = (
    <S.MenuContainer
      ref={menuRef}
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        transform: menuPosition.transform,
      }}
    >
      <h4>Construir Filtro</h4>
      <S.BuilderRow>
        <S.FilterSelectWrapper>
          <S.FilterInput
            readOnly
            value={selectedField?.label || ''}
            placeholder="Selecione um campo"
            onClick={() => setFieldsDropdownOpen(true)}
          />
          {isFieldsDropdownOpen && (
            <FieldsDropdown
              headers={availableHeaders}
              onSelect={(field) => {
                handleSelectField(field);
                setFieldsDropdownOpen(false);
              }}
              close={() => setFieldsDropdownOpen(false)}
            />
          )}
        </S.FilterSelectWrapper>
      </S.BuilderRow>

      {selectedField && selectedField.key !== 'companyTypes' && (
        <S.BuilderRow>
          <S.FilterSelectWrapper>
            <S.FilterInput
              readOnly
              value={selectedFilterType}
              placeholder="Selecione a condição"
              onClick={() => setFiltersDropdownOpen(true)}
            />
            {isFiltersDropdownOpen && (
              <FiltersDropdown
                fieldType={selectedField.type}
                fieldKey={selectedField.key}
                onSelect={(type) => {
                  handleSelectFilterType(type);
                  setFiltersDropdownOpen(false);
                }}
                close={() => setFiltersDropdownOpen(false)}
              />
            )}
          </S.FilterSelectWrapper>
        </S.BuilderRow>
      )}

      {selectedField && (
        selectedField.selectOptions ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                readOnly
                value={
                  selectedField.selectOptions.find((option) => option.value === filterValue)
                    ?.label || ''
                }
                placeholder="Selecione"
                onClick={() => setSelectOptionsDropdownOpen(true)}
              />
              {isSelectOptionsDropdownOpen && (
                <SelectOptionsDropdown
                  options={selectedField.selectOptions}
                  onSelect={(option) => {
                    setFilterValue(option.value);
                    setDisplayValue(option.label);
                    handleSelectFilterType('Igual a');
                    setSelectOptionsDropdownOpen(false);
                  }}
                  close={() => setSelectOptionsDropdownOpen(false)}
                />
              )}
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'date' && selectedFilterType === 'Intervalo' ? (
          <>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  type="date"
                  placeholder="Data inicial"
                  value={intervalValue.start}
                  onChange={e => setIntervalValue(v => ({ ...v, start: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
            <S.BuilderRow>
              <S.FilterSelectWrapper>
                <S.FilterInput
                  type="date"
                  placeholder="Data final"
                  value={intervalValue.end}
                  onChange={e => setIntervalValue(v => ({ ...v, end: e.target.value }))}
                />
              </S.FilterSelectWrapper>
            </S.BuilderRow>
          </>
        ) : selectedField.type === 'date' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type="date"
                placeholder="Selecione a data"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'price' ? (
          <S.BuilderRow style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <CurrencyInput
              id="filter-price"
              name="filter-price"
              value={priceInputVal !== '' ? parseFloat(priceInputVal) : NaN}
              onValueChange={(formatted, __, values) => {
                if (values?.float === undefined) {
                  setPriceInputVal('');
                  return;
                }
                setPriceInputVal(values.float.toString());
              }}
              style={{ flex: 1, minWidth: 0 }}
            />
            <select
              value={priceUnitVal}
              onChange={e => setPriceUnitVal(e.target.value)}
              style={{
                padding: '10px 12px',
                borderRadius: '20px',
                border: '1px solid transparent',
                backgroundColor: '#EBEBEB',
                color: '#696969',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                height: '40px',
                outline: 'none',
                width: '100px',
                flexShrink: 0
              }}
            >
              <option value="">Sem unid.</option>
              <option value="KG">Kg</option>
              <option value="MM">MM</option>
              <option value="UN">UN</option>
              <option value="M²">M²</option>
              <option value="M Linear">M Linear</option>
              <option value="M">M</option>
            </select>
          </S.BuilderRow>
        ) : selectedField.type === 'currency' ? (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <CurrencyInput
                id="filter-currency"
                name="filter-currency"
                value={filterValue !== '' ? parseFloat(filterValue) : NaN}
                onValueChange={(formatted, __, values) => {
                  if (values?.float === undefined) {
                    setFilterValue('');
                    setDisplayValue('');
                    return;
                  }
                  setFilterValue(values.float.toString());
                  setDisplayValue(values.formatted ?? formatted ?? '');
                }}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        ) : selectedField.type === 'boolean' ? (
          <S.BuilderRow style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '0 8px' }}>
            <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#696969' }}>
              {filterValue === 'false' ? 'Inativo' : 'Ativo'}
            </span>
            <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
              <input
                type="checkbox"
                checked={filterValue !== 'false'}
                onChange={(e) => {
                  const val = e.target.checked ? 'true' : 'false';
                  setFilterValue(val);
                  setDisplayValue(e.target.checked ? 'Ativo' : 'Inativo');
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span className="slider round" style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: filterValue === 'false' ? '#ccc' : '#10B981',
                transition: '0.4s',
                borderRadius: '24px'
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px', width: '18px',
                  left: filterValue === 'false' ? '3px' : '23px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.4s',
                  borderRadius: '50%'
                }} />
              </span>
            </label>
          </S.BuilderRow>
        ) : (
          <S.BuilderRow>
            <S.FilterSelectWrapper>
              <S.FilterInput
                type={selectedField.type === 'number' ? 'number' : 'text'}
                className="inline-input"
                placeholder="Digite aqui"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                inputMode={selectedField.type === 'number' ? 'decimal' : undefined}
                step={selectedField.type === 'number' ? 'any' : undefined}
              />
            </S.FilterSelectWrapper>
          </S.BuilderRow>
        )
      )}

      <S.ApplyButton onClick={onApplyAndClose}>Aplicar Filtro</S.ApplyButton>
    </S.MenuContainer>
  );

  return createPortal(menuContent, document.body);
};

export default FilterMenu;